import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks';
import { FeedbackSummaryStoreProvider } from '@modules/oracle/feedbackSummaryStore/FeedbackSummaryStoreProvider';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import {
  useGetFeedbackListSummaryByQueryContainer,
  useStopAssistantResponse,
} from '@modules/oracle/hooks';
import { Box } from '@mui/material';
import { useRef, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { ChatWithFeedbackSummaryContainer } from '../ChatWithFeedbackSummaryContainer';

export const trackFeedbackListSummaryEvents = createAnalyticsTracker(
  'feedbackListSummary',
);

interface FeedbackListSummaryContainerProps {
  queryContainer: QueryContainer;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  prompt?: string;
  onChangePrompt: (prompt?: string) => void;
}

export function FeedbackListSummaryContainerContent({
  queryContainer,
  isExpanded,
  setIsExpanded,
  prompt,
  onChangePrompt,
}: FeedbackListSummaryContainerProps) {
  const [isGenerateActionPerformed, setIsGenerateActionPerformed] =
    useState(false);

  const [shouldShowDefaultState, setShouldShowDefaultState] = useState(true);
  const [shouldShowLoader, setShouldShowLoader] = useState(false);

  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const {
    responseID,
    getFeedbackSummary,
    sessionID: isSocketConnectionEstablished,
    error,
  } = useGetFeedbackListSummaryByQueryContainer({
    queryContainer,
    shouldGetSummaryOnDemand: isChatWithFeedbackEnabled,
  });

  const [stopResponse, { isLoading: isStopResponseInProgress }] =
    useStopAssistantResponse();

  const dispatch = useFeedbackSummaryStore(({ dispatch }) => dispatch);
  const messages = useFeedbackSummaryStore(({ data }) => data.messages);
  const isCompleted = useFeedbackSummaryStore(
    ({ visual }) => visual.isCompleted,
  );
  const webSocketError = useFeedbackSummaryStore(({ visual }) => visual.error);

  const isError = Boolean(error) || Boolean(webSocketError);

  const summaryContainerRef = useRef<HTMLDivElement>(null);

  const isSummaryRendered =
    isGenerateActionPerformed && (isCompleted || messages.length > 0);

  const generateSummary = () => {
    setIsGenerateActionPerformed(true);

    dispatch({
      type: FeedbackSummaryActionType.SET_VISUAL,
      payload: {
        shouldRejectIncomingMessages: false,
      },
    });

    if (prompt) {
      dispatch({
        type: FeedbackSummaryActionType.RESET,
      });
      getFeedbackSummary(prompt);
    }

    setShouldShowLoader(true);
  };

  const onGenerateSummaryClick = () => {
    generateSummary();
    setShouldShowDefaultState(false);
    trackFeedbackListSummaryEvents({
      eventName: 'Feedback List Summary - Generate Summary Button Clicked',
    });
  };

  const onStopSummaryGeneration = async () => {
    if (!responseID) {
      return;
    }

    await stopResponse(responseID);

    setShouldShowLoader(false);

    dispatch({
      type: FeedbackSummaryActionType.RESET,
    });

    dispatch({
      type: FeedbackSummaryActionType.SET_VISUAL,
      payload: {
        shouldRejectIncomingMessages: true,
      },
    });

    setIsGenerateActionPerformed(false);
  };

  const isLoading =
    isGenerateActionPerformed &&
    !isCompleted &&
    messages.length === 0 &&
    !isError;

  return (
    <Box sx={{ overflow: 'hidden', mt: 2 }}>
      <ChatWithFeedbackSummaryContainer
        shouldShowDefaultState={shouldShowDefaultState}
        onGenerateSummaryClick={onGenerateSummaryClick}
        prompt={prompt}
        isError={isError}
        onChangePrompt={onChangePrompt}
        isSocketConnectionEstablished={isSocketConnectionEstablished}
        isStopResponseInProgress={isStopResponseInProgress}
        isGenerateActionPerformed={isGenerateActionPerformed}
        onStopSummaryGeneration={onStopSummaryGeneration}
        isLoading={isLoading}
        isSummaryRendered={isSummaryRendered}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        generateSummary={generateSummary}
        responseID={responseID}
        queryContainer={queryContainer}
        isCompleted={isCompleted}
        messages={messages}
        containerRef={summaryContainerRef}
        shouldShowLoader={shouldShowLoader}
        setShouldShowLoader={setShouldShowLoader}
      />
    </Box>
  );
}

export const FeedbackListSummaryContainer = (
  props: FeedbackListSummaryContainerProps,
) => {
  return (
    <FeedbackSummaryStoreProvider>
      <FeedbackListSummaryContainerContent {...props} />
    </FeedbackSummaryStoreProvider>
  );
};
